import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';

import type { MobileZoom } from '../../utils/canvas/canvas';
import { useTouchPan } from '../useTouchPan/useTouchPan';
import { useTouchZoom } from '../useTouchZoom/useTouchZoom';

interface UseTouchHandlersProps {
  isMobile: boolean;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  onLongPress?: (clientX: number, clientY: number) => void;
  longPressDelay?: number;
}

interface UseTouchHandlersReturn {
  mobileZoom: MobileZoom;
  setMobileZoom: React.Dispatch<React.SetStateAction<MobileZoom>>;
}

export const useTouchHandlers = ({
  isMobile,
  canvasRef,
  longPressDelay = 500,
  onLongPress,
}: UseTouchHandlersProps): UseTouchHandlersReturn => {
  const touchZoom = useTouchZoom({ canvasRef, isMobile });
  const touchPan = useTouchPan({
    canvasRef,
    isMobile,
    mobileZoom: touchZoom.mobileZoom,
    setMobileZoom: touchZoom.setMobileZoom,
  });

  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const onLongPressRef = useRef(onLongPress);
  const isManualScrollRef = useRef(false);
  const manualScrollLastYRef = useRef<number | null>(null);
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    onLongPressRef.current = onLongPress;
  }, [onLongPress]);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    touchStartPosRef.current = null;
  }, []);

  const getScrollableParent = useCallback((element: HTMLElement | null) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return null;

    let node = element?.parentElement ?? null;
    while (node) {
      const style = window.getComputedStyle(node);
      if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
        return node;
      }
      node = node.parentElement;
    }

    const scrollingElement = document.scrollingElement;
    if (scrollingElement instanceof HTMLElement) {
      return scrollingElement;
    }

    return document.body;
  }, []);

  const scrollByDelta = useCallback((deltaY: number) => {
    if (deltaY === 0) return;

    const amount = -deltaY;
    const target = scrollContainerRef.current;

    if (!target) {
      if (typeof window !== 'undefined') {
        window.scrollBy(0, amount);
      }
      return;
    }

    if (
      typeof document !== 'undefined' &&
      (target === document.body || target === document.documentElement || target === document.scrollingElement)
    ) {
      if (typeof window !== 'undefined') {
        window.scrollBy(0, amount);
      }
      return;
    }

    target.scrollTop += amount;
  }, []);

  const handleTouchStart = useCallback(
    (event: TouchEvent) => {
      if (!isMobile) return;

      if (event.touches.length === 2) {
        // Two-finger pinch to zoom
        if (event.cancelable) {
          event.preventDefault();
        }
        clearLongPressTimer();
        touchZoom.handleTouchStart(event);
        touchPan.setIsPanning(false);
      } else if (event.touches.length === 1) {
        const touch = event.touches[0];
        touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
        isManualScrollRef.current = false;
        manualScrollLastYRef.current = touch.clientY;

        // Start long-press timer
        if (onLongPressRef.current) {
          longPressTimerRef.current = setTimeout(() => {
            if (touchStartPosRef.current && onLongPressRef.current) {
              onLongPressRef.current(touchStartPosRef.current.x, touchStartPosRef.current.y);
              clearLongPressTimer();
            }
          }, longPressDelay);
        }

        // Don't start panning immediately - wait for movement to determine intent
      }
    },
    [isMobile, touchZoom, touchPan, longPressDelay, clearLongPressTimer]
  );

  const handleTouchMove = useCallback(
    (event: TouchEvent) => {
      if (!isMobile) return;

      if (isManualScrollRef.current && touchZoom.mobileZoom.scale <= 1 && event.touches.length === 1) {
        const touch = event.touches[0];
        const lastY = manualScrollLastYRef.current ?? touch.clientY;
        const deltaY = touch.clientY - lastY;

        if (deltaY !== 0) {
          if (event.cancelable) {
            event.preventDefault();
          }
          scrollByDelta(deltaY);
        }

        manualScrollLastYRef.current = touch.clientY;
        return;
      }

      // Check if finger moved too much for long-press
      if (touchStartPosRef.current && event.touches.length === 1) {
        const touch = event.touches[0];
        const deltaX = Math.abs(touch.clientX - touchStartPosRef.current.x);
        const deltaY = Math.abs(touch.clientY - touchStartPosRef.current.y);

        if (deltaX > 10 || deltaY > 10) {
          clearLongPressTimer();

          // Start panning if zoomed in and not already panning
          if (touchZoom.mobileZoom.scale > 1 && !touchPan.isPanning) {
            if (event.cancelable) {
              event.preventDefault();
            }
            // Manually initialize panning state
            touchPan.setLastPanPosition({ x: touch.clientX, y: touch.clientY });
            touchPan.setIsPanning(true);
            touchZoom.setMobileZoom((prev) => ({ ...prev, isZooming: false }));
            isManualScrollRef.current = false;
            manualScrollLastYRef.current = null;
          } else if (touchZoom.mobileZoom.scale <= 1) {
            isManualScrollRef.current = true;
            manualScrollLastYRef.current = touch.clientY;
          }
        }
      }

      if (event.touches.length === 2 && touchZoom.mobileZoom.isZooming) {
        // Two-finger pinch to zoom
        if (event.cancelable) {
          event.preventDefault();
        }
        touchZoom.handleTouchMove(event);
      } else if (event.touches.length === 1 && touchPan.isPanning && touchZoom.mobileZoom.scale > 1) {
        // Single finger pan when zoomed in
        touchPan.handleTouchMove(event);
      }
    },
    [isMobile, touchZoom, touchPan, clearLongPressTimer, scrollByDelta]
  );

  const handleTouchEnd = useCallback(
    (event: TouchEvent) => {
      if (!isMobile) return;

      clearLongPressTimer();
      touchZoom.handleTouchEnd(event);
      touchPan.handleTouchEnd(event);
      if (event.touches.length === 0) {
        isManualScrollRef.current = false;
        manualScrollLastYRef.current = null;
      }
    },
    [isMobile, touchZoom, touchPan, clearLongPressTimer]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isMobile) return;
    scrollContainerRef.current = getScrollableParent(canvas);

    const preventContextMenu = (event: Event) => {
      event.preventDefault();
    };

    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd);
    canvas.addEventListener('contextmenu', preventContextMenu);

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('contextmenu', preventContextMenu);
      clearLongPressTimer();
    };
  }, [
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    isMobile,
    canvasRef,
    clearLongPressTimer,
    getScrollableParent,
  ]);

  return {
    mobileZoom: touchZoom.mobileZoom,
    setMobileZoom: touchZoom.setMobileZoom,
  };
};
